import { Header, Footer, PageTransition } from '../components';
import { SEOHead } from '../components/seo';
import { FaUsers, FaHandshake, FaShieldAlt, FaUserShield, FaLayerGroup, FaHandHoldingHeart, FaCheck, FaArrowRight } from 'react-icons/fa';
import { useSpring, animated, useTrail } from 'react-spring';
import { useState, useEffect, useRef } from 'react';
import heroImage from '../assets/corporate_handshake.png';

function AboutPage() {
  const [heroVisible, setHeroVisible] = useState(false);
  const [introVisible, setIntroVisible] = useState(false);
  const [missionVisionVisible, setMissionVisionVisible] = useState(false);
  const [coreValuesVisible, setCoreValuesVisible] = useState(false);
  const [whyUsVisible, setWhyUsVisible] = useState(false);

  const heroRef = useRef<HTMLDivElement>(null);
  const introRef = useRef<HTMLDivElement>(null);
  const missionVisionRef = useRef<HTMLDivElement>(null);
  const coreValuesRef = useRef<HTMLDivElement>(null);
  const whyUsRef = useRef<HTMLDivElement>(null);

  const coreValuesData = [
    { title: "Commitment", icon: FaHandHoldingHeart, num: "01" },
    { title: "Transparency", icon: FaLayerGroup, num: "02" },
    { title: "Team Work", icon: FaUsers, num: "03" },
    { title: "Mutual Respect", icon: FaHandshake, num: "04" },
    { title: "Trust", icon: FaShieldAlt, num: "05" },
    { title: "Honesty", icon: FaUserShield, num: "06" },
  ];

  // Intersection Observer for all sections
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const target = entry.target;
            if (target === heroRef.current) setHeroVisible(true);
            if (target === introRef.current) setIntroVisible(true);
            if (target === missionVisionRef.current) setMissionVisionVisible(true);
            if (target === coreValuesRef.current) setCoreValuesVisible(true);
            if (target === whyUsRef.current) setWhyUsVisible(true);
            observer.unobserve(target);
          }
        });
      },
      { threshold: 0.15 }
    );

    const refs = [heroRef, introRef, missionVisionRef, coreValuesRef, whyUsRef];
    refs.forEach(ref => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, []);

  // Hero section animations
  const heroTextAnimation = useSpring({
    opacity: heroVisible ? 1 : 0,
    transform: heroVisible ? 'translateX(0px)' : 'translateX(-50px)',
    config: { mass: 1, tension: 120, friction: 30 },
  });

  const heroImageAnimation = useSpring({
    opacity: heroVisible ? 1 : 0,
    transform: heroVisible ? 'scale(1)' : 'scale(1.1)',
    config: { mass: 1, tension: 120, friction: 30 },
  });

  // Intro section animation
  const introAnimation = useSpring({
    opacity: introVisible ? 1 : 0,
    transform: introVisible ? 'translateY(0px)' : 'translateY(40px)',
    config: { mass: 1, tension: 120, friction: 30 },
  });

  // Mission & Vision animations
  const missionAnimation = useSpring({
    opacity: missionVisionVisible ? 1 : 0,
    transform: missionVisionVisible ? 'translateX(0px)' : 'translateX(-60px)',
    config: { mass: 1, tension: 120, friction: 30 },
    delay: 0,
  });

  const visionAnimation = useSpring({
    opacity: missionVisionVisible ? 1 : 0,
    transform: missionVisionVisible ? 'translateX(0px)' : 'translateX(60px)',
    config: { mass: 1, tension: 120, friction: 30 },
    delay: 300,
  });

  // Staggered animation for core values cards
  const trail = useTrail(coreValuesData.length, {
    opacity: coreValuesVisible ? 1 : 0,
    transform: coreValuesVisible ? 'translateY(0px)' : 'translateY(40px)',
    config: { mass: 1, tension: 180, friction: 40 },
  });

  // Why Choose Us list items animation
  const whyUsItems = [
    "High-quality products that meet strict safety standards.",
    "Operating in accordance with all rules and regulations.",
    "Adapting product offerings to meet customer needs.",
    "Flexible payment terms for easy purchase.",
    "Well-connected global supply chain for efficient delivery.",
    "Commitment to customer satisfaction and going the extra mile.",
    "Up-to-date delivery tracking for customers."
  ];

  const whyUsTrail = useTrail(whyUsItems.length, {
    opacity: whyUsVisible ? 1 : 0,
    transform: whyUsVisible ? 'translateX(0px)' : 'translateX(-30px)',
    config: { mass: 1, tension: 180, friction: 40 },
  });

  return (
    <div className="min-h-screen bg-white">
      <SEOHead />
      <Header />
      <PageTransition>
        <main className="pt-16">
          {/* Bold Split Header */}
          <div className="flex flex-col md:flex-row min-h-[500px] md:h-[80vh]" ref={heroRef}>
            <animated.div style={heroTextAnimation} className="w-full md:w-1/2 bg-gray-900 text-white flex flex-col justify-center px-6 py-16 md:px-20 md:py-0 relative overflow-hidden">
              <h1 className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tighter leading-none mb-4 uppercase">
                About<br /><span className="text-orange-500">Us</span>
              </h1>
              <div className="w-24 h-2 bg-white mt-6 md:mt-8 mb-6 md:mb-8"></div>
              <p className="text-lg md:text-xl lg:text-2xl text-gray-300 font-light max-w-md">
                Pioneering global supply chain solutions since 2015.
              </p>
              {/* Decorative giant number */}
              <span className="absolute -bottom-20 -right-10 text-[20rem] font-bold text-black select-none z-0 opacity-20 hidden md:block">
                01
              </span>
            </animated.div>
            <animated.div style={heroImageAnimation} className="w-full md:w-1/2 relative bg-gray-900 min-h-[300px] md:min-h-0">
              <div className="absolute inset-0 bg-orange-500/10 mix-blend-overlay z-10"></div>
              <img
                src={heroImage}
                alt="Logistics Operations"
                className="w-full h-full object-cover grayscale contrast-125"
              />
            </animated.div>
          </div>

          {/* Minimal Intro */}
          <div className="py-12 md:py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" ref={introRef}>
            <animated.div style={introAnimation} className="max-w-4xl">
              <h2 className="text-2xl md:text-4xl lg:text-5xl font-bold leading-tight text-gray-900 mb-6 md:mb-8">
                Founded in Dubai, driven by a vision to <span className="text-orange-500">connect the world.</span>
              </h2>
              <p className="text-lg md:text-xl text-gray-600 leading-relaxed border-l-4 border-orange-500 pl-4 md:pl-6">
                Easy Buy General Trading L.L.C is a service & supply–oriented company established to create general trading and supply solutions in a wide range of products from the root source across the world. We offer a dedicated team of experts to ensure the best service, information, and complete one-stop solutions.
              </p>
            </animated.div>
          </div>

          {/* Mission & Vision - Asymmetrical Grid */}
          <div className="border-y border-gray-200" ref={missionVisionRef}>
            <div className="grid grid-cols-1 md:grid-cols-12 md:min-h-[600px]">
              {/* Mission */}
              <animated.div style={missionAnimation} className="col-span-12 md:col-span-7 bg-orange-500 p-8 md:p-16 lg:p-24 flex flex-col justify-center relative group">
                <span className="text-6xl md:text-8xl font-bold text-black opacity-10 absolute top-4 left-4 group-hover:opacity-20 transition-opacity hidden md:block">MISSION</span>
                <h3 className="text-3xl md:text-4xl font-bold uppercase text-white mb-6 md:mb-8 relative z-10">Our Mission</h3>
                <p className="text-lg md:text-xl lg:text-2xl font-medium text-white/90 leading-relaxed relative z-10">
                  We have a dedicated team of experts working around the clock to make sure that our customers get the best service, information, and a complete one stop solution for all their requirement.
                </p>
                <FaArrowRight className="text-4xl text-white mt-8 transform group-hover:translate-x-4 transition-transform" />
              </animated.div>

              {/* Vision */}
              <animated.div style={visionAnimation} className="col-span-12 md:col-span-5 bg-slate-50 p-8 md:p-16 lg:p-24 flex flex-col justify-center border-l-0 md:border-l border-gray-200 relative">
                <span className="text-6xl md:text-8xl font-bold text-gray-200 absolute bottom-4 right-4 hidden md:block">VIS</span>
                <h3 className="text-3xl md:text-4xl font-bold uppercase text-gray-900 mb-6 md:mb-8">Vision</h3>
                <p className="text-base md:text-lg text-gray-600 leading-relaxed">
                  We have a dedicated team of experts working around the clock to make sure that our customers get the best service, information, and a complete one stop solution for all their requirement.
                </p>
              </animated.div>
            </div>
          </div>

          {/* Core Values - Numbered Tiles */}
          <div className="bg-white py-12 md:py-24 px-4 sm:px-6 lg:px-8" ref={coreValuesRef}>
            <div className="max-w-7xl mx-auto">
              <div className="flex items-baseline justify-between mb-12 md:mb-16">
                <h2 className="text-3xl md:text-5xl lg:text-6xl font-bold text-gray-900 uppercase tracking-tight">Core <span className="text-orange-500">Values</span></h2>
                <p className="text-xl font-bold text-gray-400 hidden md:block">/// EST 2015</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {trail.map((style, idx) => {
                  const val = coreValuesData[idx];
                  return (
                    <animated.div
                      key={idx}
                      style={style}
                      className="bg-white border border-gray-100 shadow-sm p-8 md:p-12 h-64 md:h-80 relative group hover:bg-gray-900 transition-colors duration-300 flex flex-col justify-between overflow-hidden rounded-xl"
                    >
                      <span className="text-6xl md:text-8xl font-bold text-gray-100 absolute -right-4 -top-8 group-hover:text-gray-800 transition-colors z-0">{val.num}</span>
                      <val.icon className="text-3xl md:text-4xl text-orange-500 relative z-10" />
                      <h3 className="text-2xl md:text-3xl font-bold text-gray-900 group-hover:text-white relative z-10 uppercase">{val.title}</h3>
                      <div className="w-12 h-1 bg-orange-500 group-hover:w-full transition-all duration-300"></div>
                    </animated.div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Why Easy Buy - Editorial List */}
          <div className="bg-gray-950 text-white py-16 md:py-24 lg:py-32" ref={whyUsRef}>
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 lg:gap-16">
                <div className="col-span-12 lg:col-span-4">
                  <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 md:mb-8 leading-tight md:leading-none">WHY<br />CHOOSE<br /><span className="text-orange-500">US?</span></h2>
                  <p className="text-gray-400 text-base md:text-lg">Delivering excellence across borders with uncompromised quality and speed.</p>
                </div>
                <div className="col-span-12 lg:col-span-8">
                  <ul className="space-y-0">
                    {whyUsTrail.map((style, i) => (
                      <animated.li key={i} style={style} className="py-4 md:py-8 border-b border-gray-800 flex items-start group hover:pl-4 md:hover:pl-8 transition-all duration-300 cursor-default">
                        <span className="text-orange-500 mr-3 md:mr-6 mt-1 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0">
                          <FaCheck className="w-4 h-4 md:w-5 md:h-5" />
                        </span>
                        <span className="text-base md:text-xl lg:text-2xl xl:text-3xl font-light group-hover:text-orange-500 transition-colors leading-relaxed">{whyUsItems[i]}</span>
                      </animated.li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </main>
      </PageTransition>
      <Footer />
    </div>
  );
}

export default AboutPage;